import { useEffect, useState } from "react";
import axios from "axios";
import { Rocket, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { mailPromo } from "@/lib/analysisMail";
import { API } from "@/lib/api";

const HIDE_KEY = "aurora_promo_hidden";

const left = (iso) => {
  const ms = Math.max(0, new Date(iso) - Date.now());
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5), m = Math.floor((ms % 36e5) / 6e4), s = Math.floor((ms % 6e4) / 1e3);
  return { ms, parts: [[d, "d"], [h, "h"], [m, "m"], [s, "s"]] };
};

const Countdown = ({ iso }) => {
  const { tr } = useLang();
  const units = { d: tr("giorni", "days"), h: tr("ore", "hours"), m: tr("min", "min"), s: tr("sec", "sec") };
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  return (
    <div data-testid="promo-countdown" className="flex gap-2">
      {left(iso).parts.map(([v, u]) => (
        <div key={u} className="min-w-[56px] text-center border border-white/20 bg-white/5 px-2 py-2">
          <div className="font-display text-2xl tabular-nums">{String(v).padStart(2, "0")}</div>
          <div className="text-[9px] uppercase tracking-widest font-mono text-white/60">{units[u]}</div>
        </div>
      ))}
    </div>
  );
};

export const PromoBanner = ({ data }) => {
  const { tr } = useLang();
  const [promo, setPromo] = useState(data.promo_expires_at ? { promo_expires_at: data.promo_expires_at, active: true } : null);
  const [pack, setPack] = useState("new");
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(HIDE_KEY) === "1");
  useEffect(() => {
    if (!data.id) return;
    axios.get(`${API}/analyses/${data.id}/promo`).then((r) => setPromo(r.data)).catch(() => {});
  }, [data.id]);
  if (hidden || !promo?.active || left(promo.promo_expires_at).ms <= 0) return null;
  const close = () => { sessionStorage.setItem(HIDE_KEY, "1"); setHidden(true); };
  const request = () => { mailPromo(data, pack); toast.success(tr("Richiesta preparata — controlla la tua email.", "Request prepared — check your email.")); };
  return (
    <section data-testid="promo-banner" className="relative overflow-hidden bg-[#2B3036] text-white border-l-4 border-[#E0262C] animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <button data-testid="promo-close-x" onClick={close} className="absolute top-4 right-4 z-10 text-white/50 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
      <div className="relative grid lg:grid-cols-12 gap-8 p-8 lg:p-10">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-[#E0262C] mb-3">
            <Rocket className="w-3.5 h-3.5" /> {tr("Solo per i prossimi 15 giorni", "Only for the next 15 days")}
          </div>
          <h3 className="font-display text-3xl leading-tight mb-3">{tr("Rifai il tuo sito con noi", "Rebuild your website with us")}</h3>
          <p className="text-sm text-[#c4c9ce] leading-relaxed max-w-xl">
            {tr("Hai appena visto cosa non funziona. Ti proponiamo di rifare il sito da zero (WEB/APP professionale) oppure un restyling completo di quello che abbiamo analizzato.",
              "You've just seen what isn't working. We offer to rebuild your website from scratch (professional WEB/APP) or a complete restyling of the one we analysed.")}
          </p>
          <div className="flex flex-wrap gap-2 mt-5" role="radiogroup">
            {[["new", tr("Nuovo sito WEB/APP", "New WEB/APP site")], ["restyling", tr("Restyling del sito analizzato", "Restyling of the analysed site")]].map(([v, l]) => (
              <button key={v} data-testid={`promo-pack-${v}`} onClick={() => setPack(v)} role="radio" aria-checked={pack === v}
                className={`px-3 py-1.5 text-xs border transition-colors ${pack === v ? "bg-white text-[#2B3036] border-white" : "border-white/30 text-white/80 hover:border-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-col justify-between gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-white/60 mb-1">{tr("Promozione lancio", "Launch offer")}</div>
            <div data-testid="promo-price" className="font-display text-5xl"><span className="aurora-text">2 €</span><span className="text-lg text-white/70"> {tr("al giorno", "per day")}</span></div>
            <div className="text-[11px] text-white/60 mt-1 mb-3">{tr("Offerta valida 15 giorni dall'analisi.", "Offer valid for 15 days from the analysis.")}</div>
            <Countdown iso={promo.promo_expires_at} />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button data-testid="promo-request-btn" onClick={request}
              className="inline-flex items-center justify-center gap-2 aurora-gradient text-white px-5 py-3 text-sm font-medium hover:opacity-90 transition-opacity">
              <Mail className="w-4 h-4" /> {tr("Richiedo info sulla promozione 2 € al giorno", "Request info on the 2 € per day offer")}
            </button>
            <button data-testid="promo-skip-btn" onClick={close} className="px-4 py-3 text-xs text-white/70 hover:text-white border border-white/20 hover:border-white transition-colors">
              {tr("Continua senza promozione", "Continue without the offer")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
